// src/components/UserActionsMenu.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  LockReset as ResetIcon,
} from '@mui/icons-material';
import { deleteUser, adminResetPassword } from '../services/userService';

const UserActionsMenu = ({
  anchorEl,
  user,          // may become null after menu closes — we capture it internally
  onClose,       // closes the Menu anchor (does NOT clear menuUser in parent)
  onFullClose,   // called when ALL dialogs are done — parent clears menuUser
  onEdit,
  onSuccess,
  onNotify,      // (message, severity) — shows a Snackbar in the parent
}) => {
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // FIX: Capture the user when the menu opens into a ref.
  // The parent may set user=null when the Menu closes (to clean up anchorEl),
  // but the dialogs that open afterwards still need the original user object.
  // Using a ref means we never lose it mid-flight.
  const capturedUser = useRef(null);

  useEffect(() => {
    if (user) {
      // Menu is opening (or staying open) with a real user — capture it
      capturedUser.current = user;
    }
  }, [user]);

  // Helper: close everything and tell parent it's fully safe to clear menuUser
  const fullClose = () => {
    setResetDialogOpen(false);
    setDeleteDialogOpen(false);
    setNewPassword('');
    setError('');
    setLoading(false);
    if (onFullClose) onFullClose();
  };

  const handleEdit = () => {
    onClose();           // close the Menu
    onEdit(capturedUser.current);
    // Don't call fullClose — EditUserDialog is managed by the parent,
    // and menuUser must survive until parent's EditUserDialog onSuccess fires.
    if (onFullClose) onFullClose();
  };

  const handleOpenReset = () => {
    onClose();           // close the Menu anchor — but capturedUser.current is safe
    setError('');
    setNewPassword('');
    setResetDialogOpen(true);
  };

  const handleOpenDelete = () => {
    onClose();           // close the Menu anchor
    setError('');
    setDeleteDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword) {
      setError('Password is required');
      return;
    }

    // FIX: Use capturedUser.current — NOT the `user` prop which may be null now
    const targetUser = capturedUser.current;
    if (!targetUser?.id) {
      setError('Unable to identify user. Please close and try again.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await adminResetPassword(targetUser.id, newPassword);
      const fullName = `${targetUser.firstName || ''} ${targetUser.lastName || ''}`.trim();
      await onSuccess();    // refresh the table (stays on current page)
      if (onNotify) onNotify(`Password reset successfully for ${fullName}`, 'success');
      fullClose();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to reset password';
      setError(msg);
      if (onNotify) onNotify(msg, 'error');
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    // FIX: Use capturedUser.current — NOT the `user` prop
    const targetUser = capturedUser.current;
    if (!targetUser?.id) {
      setError('Unable to identify user. Please close and try again.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await deleteUser(targetUser.id);
      const fullName = `${targetUser.firstName || ''} ${targetUser.lastName || ''}`.trim();
      await onSuccess();    // refresh the table
      if (onNotify) onNotify(`User ${fullName} deactivated successfully`, 'success');
      fullClose();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to delete user';
      setError(msg);
      if (onNotify) onNotify(msg, 'error');
      setLoading(false);
    }
  };

  // The displayed name in the delete confirmation comes from capturedUser.current
  const displayUser = capturedUser.current;

  return (
    <>
      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit User</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleOpenReset}>
          <ListItemIcon>
            <ResetIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Reset Password</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleOpenDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete User</ListItemText>
        </MenuItem>
      </Menu>

      {/* Reset Password Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => !loading && fullClose()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            fullWidth
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
            margin="normal"
            disabled={loading}
            helperText="User will be required to change password on next login"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !loading && fullClose()} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleResetPassword}
            variant="contained"
            disabled={loading || !newPassword}
          >
            {loading ? <CircularProgress size={24} /> : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !loading && fullClose()}
        maxWidth="sm"
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete{' '}
            <strong>{displayUser?.firstName} {displayUser?.lastName}</strong>?
            This action will deactivate the user account.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !loading && fullClose()} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserActionsMenu;